import React, { useRef, useEffect, useMemo } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';

interface RichTextFieldProps {
  value: string;
  placeholder?: string;
  rows?: number;
  className?: string;
  id?: string;
}

/**
 * Rich text editor using Quill
 * 
 * Simple uncontrolled component - parent reads value via ref.getValue() when needed
 */
export const RichTextField = React.forwardRef<
  { getValue: () => string },
  RichTextFieldProps
>(({ value, placeholder = '', rows = 3, className, id }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'bold': true }, { 'italic': true }, { 'underline': true }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ],
  }), []);

  // Convert Quill's list format to standard HTML (for saving)
  // Quill uses <ol> with data-list="bullet" for bullet lists, we convert to <ul>
  // Standard <ol> (without data-list="bullet") are kept as ordered lists
  const convertQuillToStandardHTML = React.useCallback((html: string): string => {
    let converted = html;
    
    // Remove ql-ui spans first
    converted = converted.replace(/<span\s+class="ql-ui"[^>]*><\/span>/gi, '');
    
    // Find all <ol> tags and check if they contain bullet list items
    converted = converted.replace(/<ol>([\s\S]*?)<\/ol>/gi, (match, content) => {
      // Check if this ol contains bullet list items (has data-list="bullet" on any li)
      if (content.includes('data-list="bullet"')) {
        // Convert to ul - remove data-list attributes from li elements
        const ulContent = content.replace(/<li\s+data-list="bullet"[^>]*>/gi, '<li>');
        return `<ul>${ulContent}</ul>`;
      }
      // Keep as ol for ordered lists (no data-list="bullet")
      return `<ol>${content}</ol>`;
    });
    
    // Remove any remaining data-list attributes
    converted = converted.replace(/\s+data-list="[^"]*"/gi, '');
    
    return converted;
  }, []);

  // Convert standard HTML to Quill's format (for loading into editor)
  // Convert <ul> back to <ol> with data-list="bullet"
  // Keep <ol> as is (for ordered lists)
  const convertStandardToQuillHTML = React.useCallback((html: string): string => {
    let converted = html;
    
    // Convert <ul> to <ol> with data-list="bullet" on each li
    converted = converted.replace(/<ul>([\s\S]*?)<\/ul>/gi, (match, content) => {
      // Convert each <li> to have data-list="bullet"
      const liContent = content.replace(/<li([^>]*)>/gi, '<li data-list="bullet"$1>');
      return `<ol>${liContent}</ol>`;
    });
    
    // <ol> without data-list="bullet" stays as <ol> (ordered list)
    // No conversion needed for ordered lists
    
    return converted;
  }, []);

  // Expose getValue method - reads directly from Quill and converts to standard HTML
  React.useImperativeHandle(ref, () => ({
    getValue: () => {
      if (!quillRef.current) {
        console.warn('[RichTextField] Quill not initialized yet');
        return '';
      }
      const html = quillRef.current.root.innerHTML;
      // Clean up empty content
      if (html === '<p><br></p>' || html === '<p></p>') return '';
      // Convert Quill format to standard HTML (for saving)
      const converted = convertQuillToStandardHTML(html);
      return converted.trim();
    },
  }));

  // Initialize Quill once
  useEffect(() => {
    if (!editorRef.current || quillRef.current) return;

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      placeholder,
      modules,
      formats: ['bold', 'italic', 'underline', 'list', 'indent'],
    });

    quillRef.current = quill;

    // Set initial value - convert standard HTML to Quill format
    if (value) {
      const quillFormat = convertStandardToQuillHTML(value);
      // Use clipboard to properly convert HTML to Quill's internal format
      const delta = quill.clipboard.convert({ html: quillFormat });
      quill.setContents(delta, 'silent');
    }
  }, [convertStandardToQuillHTML]);

  // Update when value prop changes (for editing existing content)
  useEffect(() => {
    if (!quillRef.current) return;
    
    // Convert standard HTML to Quill format before setting
    const quillFormat = value ? convertStandardToQuillHTML(value) : '';
    
    // Get current content and convert to standard HTML for comparison
    const currentHTML = quillRef.current.root.innerHTML;
    const currentStandard = convertQuillToStandardHTML(currentHTML);
    
    // Only update if the standard HTML versions differ
    if (currentStandard !== value) {
      // Use clipboard to properly convert HTML to Quill's internal format
      const delta = quillRef.current.clipboard.convert({ html: quillFormat });
      quillRef.current.setContents(delta, 'silent');
    }
  }, [value, convertStandardToQuillHTML, convertQuillToStandardHTML]);

  const minHeight = `${rows * 1.5 * 16}px`;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="rich-text-field-wrapper" style={{ minHeight }}>
        <div id={id} ref={editorRef} />
      </div>
      <p className="text-xs text-muted-foreground">
        Bruk toolbar for formatering • Enter for ny linje
      </p>
    </div>
  );
});

RichTextField.displayName = 'RichTextField';
